import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { actionPlanApi } from '../api/actionPlanApi';
import { useGetAssessmentPeriodMetricResultsQuery } from 'areas/assessment-schedule/api';
import { useApiPortalV1AssessmentsTemplatesListQuery } from 'store/generated/portal/assessments';
import { RecommendationTemplateRead } from 'store/generated/portal/assessments';

import { PageSection, BodyText, PageHeader } from 'common/styles/page';
import { Card } from 'common/components/Card';
import { StyledButton } from 'common/components/button/StyledButton';

import SelectGoals from './SelectGoals';
import OrgGoalsHeader from './OrganizationalGoalHeader';
import { Icon } from 'semantic-ui-react';
import LoadingSpinner from 'common/components/LoadingSpinner';
import {
  getSelectedAssessmentPeriodId,
  getCurrentOrganizationId,
  getMemberId,
} from 'common/selectors/auth';
import { RecommendationType } from 'common/constants/RecommendationType';

interface OrgAssignGoalsProps {}

const OrgAssignGoals: React.FC<OrgAssignGoalsProps> = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const organizationId = useSelector(getCurrentOrganizationId);
  const memberId = useSelector(getMemberId);

  const { data: pillarData } = useGetAssessmentPeriodMetricResultsQuery(
    { assessmentPeriodId: assessmentPeriodId },
    { skip: !assessmentPeriodId }
  );
  const { data: goal_template_data } = useApiPortalV1AssessmentsTemplatesListQuery({
    typeOf: RecommendationType.ORGANIZATIONAL_GOALS,
  });

  const [createAssessmentPlan] =
    actionPlanApi.useApiPortalV1AssessmentsPlansOrganizationCreateMutation({});

  const [selectedGoalIds, setSelectedGoalIds] = useState<number[]>([-1, -1, -1]);
  const [hasStrengthGoal, setHasStrengthGoal] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Genereate Project Plan
  const onSave = async () => {
    if (!goal_template_data || goal_template_data.length === 0) return;

    const templates = goal_template_data.map((item: RecommendationTemplateRead) => ({
      id: item.id,
      name: item.name || 'Unnamed Template',
      template: item.template || null,
    }));

    const matchingTemplates = templates.filter(template => selectedGoalIds.includes(template.id));

    setLoading(true);

    try {
      await Promise.all(
        matchingTemplates.map(template =>
          createAssessmentPlan({
            organizationAssessmentPeriodActionPlan: {
              organization: organizationId,
              assessment_period_id: assessmentPeriodId,
              leader: memberId,
              recommendation_name: template.name,
              recommendation_json: template.template,
              recommendation: template.id,
              type_of: RecommendationType.ORGANIZATIONAL_GOALS,
            },
          }).unwrap()
        )
      );
    } catch (err) {
      console.error('Plan creation failed', err);
    } finally {
      dispatch(actionPlanApi.util.resetApiState());
      setLoading(false);
      navigate('/app/org-goals');
    }
  };

  const isDataLoading =
    loading ||
    !pillarData ||
    !pillarData.pillars?.length ||
    !goal_template_data ||
    !Array.isArray(goal_template_data) ||
    goal_template_data.length === 0;

  if (isDataLoading) return <LoadingSpinner />;

  return (
    <>
      <PageSection>
        <OrgGoalsHeader />
      </PageSection>

      <PageSection padtop />
      <Card>
        <PageSection>
          <PageHeader
            title="Select Organizational Goals"
            controls={
              <StyledButton
                disabled={
                  loading ||
                  selectedGoalIds.includes(-1) ||
                  selectedGoalIds.some((id, i) => selectedGoalIds.indexOf(id) !== i)
                }
                color={selectedGoalIds.includes(-1) ? 'gray' : 'green'}
                label={loading ? 'Generating...' : 'Generate Project Plan'}
                width="200px"
                onClick={onSave}
              />
            }
          />
          <PageSection>
            <BodyText>
              Select goals for the upcoming Discover, Design and Deploy phases of organizational
              improvement. Two goals will be selected to support the pillars that had the lowest
              overall average scores
              {hasStrengthGoal &&
                ", while the third goal will reinforce the organization's greatest strength"}
              .
            </BodyText>
          </PageSection>
          <PageSection padtop>
            <BodyText>
              Goals marked with a star <Icon name="star outline" color="grey" />
              are the most impactful, while those without a star will have an positive impact to the
              pillar, but less-directly.
            </BodyText>
          </PageSection>
          {!selectedGoalIds.includes(-1) &&
            selectedGoalIds.some((id, i) => selectedGoalIds.indexOf(id) !== i) && (
              <PageSection padtop>
                <BodyText color="red">
                  Organization goals must be distinct across pillars. Please select unique goals.
                </BodyText>
              </PageSection>
            )}
        </PageSection>

        <PageSection padleft padright>
          <SelectGoals
            selectedGoalIds={selectedGoalIds}
            setSelectedGoalIds={setSelectedGoalIds}
            pillar_data={pillarData}
            rec_template_data={goal_template_data}
            setHasStrengthGoal={setHasStrengthGoal}
          />
        </PageSection>
      </Card>
    </>
  );
};

export default OrgAssignGoals;

